
import React, { memo } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutGrid, LayoutList } from "lucide-react";
import { tabItemVariants } from "@/components/ui/toggle";
import { LIST_NAMES, type MovieListName, LIST_LABELS } from "@/hooks/search/types/listConstants";

interface TabsNavigationProps {
  currentTab: MovieListName;
  viewMode: "grid" | "list";
  onViewModeChange: (mode: "grid" | "list") => void;
  onTabChange: (value: MovieListName) => void;
}

// Using memo to prevent unnecessary re-renders
const TabsNavigation = memo<TabsNavigationProps>(({ 
  currentTab, 
  viewMode, 
  onViewModeChange,
  onTabChange
}) => {
  return (
    <div className="bg-transparent pt-2 pb-3 md:py-1 md:pb-4">
      <div className="container mx-auto">
        {/* Updated layout to use row by default with column for very small screens */}
        <div className="flex max-[550px]:flex-col min-[550px]:flex-row min-[550px]:justify-between min-[550px]:items-center gap-3 max-[550px]:gap-3">
          {/* Main tabs navigation - left side on larger screens, full width on mobile */}
          <div className="w-full min-[550px]:w-auto order-2 min-[550px]:order-1">
            <Tabs value={currentTab} onValueChange={onTabChange} className="w-full md:w-auto">
              <TabsList className="bg-transparent h-9 p-1.5 w-full justify-center md:justify-start">
                <TabsTrigger 
                  value={LIST_NAMES.FAVORITES}
                  className="rounded-full h-8 md:h-9 flex-1 md:flex-none md:min-w-[120px]"
                >
                  <span className={tabItemVariants({
                    state: currentTab === LIST_NAMES.FAVORITES ? 'active' : 'inactive'
                  })}>
                    <svg className={`h-3.5 w-3.5 md:h-4 md:w-4 ${currentTab === LIST_NAMES.FAVORITES ? 'animate-pulse' : ''}`} 
                        fill={currentTab === LIST_NAMES.FAVORITES ? '#f8f9fa' : 'none'} 
                        stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span className="whitespace-nowrap text-xs md:text-sm">{LIST_LABELS[LIST_NAMES.FAVORITES]}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger 
                  value={LIST_NAMES.UNSEEN_GEMS}
                  className="rounded-full h-8 md:h-9 flex-1 md:flex-none md:min-w-[140px]"
                >
                  <span className={tabItemVariants({
                    state: currentTab === LIST_NAMES.UNSEEN_GEMS ? 'active' : 'inactive'
                  })}>
                    <svg className={`h-3.5 w-3.5 md:h-4 md:w-4 ${currentTab === LIST_NAMES.UNSEEN_GEMS ? 'animate-pulse' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    <span className="whitespace-nowrap text-xs md:text-sm">{LIST_LABELS[LIST_NAMES.UNSEEN_GEMS]}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger 
                  value={LIST_NAMES.WATCHLIST}
                  className="rounded-full h-8 md:h-9 flex-1 md:flex-none md:min-w-[120px]"
                >
                  <span className={tabItemVariants({
                    state: currentTab === LIST_NAMES.WATCHLIST ? 'active' : 'inactive'
                  })}>
                    <svg className={`h-3.5 w-3.5 md:h-4 md:w-4 ${currentTab === LIST_NAMES.WATCHLIST ? 'animate-pulse' : ''}`}
                        fill={currentTab === LIST_NAMES.WATCHLIST ? '#f8f9fa' : 'none'} 
                        stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    <span className="whitespace-nowrap text-xs md:text-sm">{LIST_LABELS[LIST_NAMES.WATCHLIST]}</span>
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* View mode toggle - right side on larger screens, top on mobile */}
          <div className="min-[550px]:w-auto order-1 min-[550px]:order-2 flex justify-end">
            <Tabs 
              value={viewMode} 
              onValueChange={(value) => value && onViewModeChange(value as "grid" | "list")}
            >
              <TabsList className="bg-transparent h-9 p-1.5">
                <TabsTrigger value="grid" className="rounded-full h-8 min-w-[80px] md:min-w-[90px]">
                  <span className={tabItemVariants({
                    state: viewMode === 'grid' ? 'active' : 'inactive'
                  })}>
                    <LayoutGrid className={`h-3.5 w-3.5 md:h-4 md:w-4 ${viewMode === 'grid' ? 'animate-pulse' : ''}`} />
                    <span className="text-xs md:text-sm whitespace-nowrap">Grid</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="list" className="rounded-full h-8 min-w-[80px] md:min-w-[90px]">
                  <span className={tabItemVariants({
                    state: viewMode === 'list' ? 'active' : 'inactive'
                  })}>
                    <LayoutList className={`h-3.5 w-3.5 md:h-4 md:w-4 ${viewMode === 'list' ? 'animate-pulse' : ''}`} />
                    <span className="text-xs md:text-sm whitespace-nowrap">List</span>
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
});

TabsNavigation.displayName = "TabsNavigation";

export default TabsNavigation;
